import { forwardRef, useCallback, useRef } from 'react';
import classnames from 'classnames';

import { keyboardMatches, keyboardKeys } from '@hh.ru/magritte-common-keyboard';
import { isEventFromInteractiveComponent } from '@hh.ru/magritte-common-mouse';
import { DisabledContext, useDisabled } from '@hh.ru/magritte-common-use-disabled';
import type { PolymorphicForwardRefRenderFunc, PolymorphicComponentWithRef } from '@hh.ru/magritte-types';
import { CardSkeleton } from '@hh.ru/magritte-ui-card/Skeleton';
import type { CardBorderRadius, CardPadding, CardProps } from '@hh.ru/magritte-ui-card/types';
import { IconDynamic } from '@hh.ru/magritte-ui-icon';
import { enrichWithSkeleton } from '@hh.ru/magritte-ui-skeleton';
import { TextDynamic } from '@hh.ru/magritte-ui-typography';

import styles from './card.less';

const borderSides = ['top-left', 'top-right', 'bottom-right', 'bottom-left'];
const genBorderRadiusProp = (radiusProps: {
    borderRadius: CardBorderRadius;
    borderTopLeftRadius?: CardBorderRadius;
    borderTopRightRadius?: CardBorderRadius;
    borderBottomLeftRadius?: CardBorderRadius;
    borderBottomRightRadius?: CardBorderRadius;
}) => {
    const isHalfHeight = Object.values(radiusProps).includes('halfHeight');
    const borderRadius = isHalfHeight ? 99999 : radiusProps.borderRadius;
    const { borderTopLeftRadius, borderTopRightRadius, borderBottomLeftRadius, borderBottomRightRadius } =
        Object.fromEntries(
            Object.entries(radiusProps)
                .map((radius) => (isHalfHeight ? [radius[0], undefined] : radius))
                .filter((radius) => radius[1] !== undefined)
        ) as typeof radiusProps;

    const radius = [borderTopLeftRadius, borderTopRightRadius, borderBottomRightRadius, borderBottomLeftRadius];

    const tableVariables = radius.reduce(
        (acc, val, index) =>
            val === undefined
                ? acc
                : {
                      ...acc,
                      [`--magritte-ui-table-border-radius-${borderSides[index]}`]: `${val}px`,
                  },
        {}
    );

    return {
        borderRadius: `${radius.map((radius) => radius ?? borderRadius).join('px ')}px`,
        '--magritte-ui-table-border-radius': `${borderRadius}px`,
        ...tableVariables,
    };
};

const genPaddingProps = (paddingProps: {
    Left?: CardPadding;
    Right?: CardPadding;
    Top?: CardPadding;
    Bottom?: CardPadding;
}) =>
    Object.entries(paddingProps).reduce<Record<string, number>>(
        (acc, [key, value]) => (value !== undefined ? { ...acc, [`padding${key}`]: value } : acc),
        {}
    );

const CardForwardRefRenderFunc: PolymorphicForwardRefRenderFunc<CardProps, 'div'> = (
    {
        Element = 'div',
        stretched = false,
        verticalStretched = false,
        hoverEnabled = false,
        pressEnabled = false,
        shadow = 'level-0',
        increaseShadow = false,
        onClick,
        style = 'primary',
        hoverStyle,
        disabled = false,
        padding = 0,
        paddingLeft,
        paddingRight,
        paddingTop,
        paddingBottom,
        borderRadius = 0,
        borderTopLeftRadius,
        borderTopRightRadius,
        borderBottomLeftRadius,
        borderBottomRightRadius,
        children,
        borderStyle,
        borderWidth = 'none',
        ...restProps
    },
    ref
) => {
    const forceDisabled = useDisabled();
    const hasFocusRef = useRef(false);
    const focusCallback = () => {
        hasFocusRef.current = true;
    };
    const blurCallback = () => {
        hasFocusRef.current = false;
    };
    const activationHandler = useCallback(
        (event: React.KeyboardEvent<HTMLElement> | React.MouseEvent<HTMLElement>) => {
            if (disabled || forceDisabled || !onClick) {
                return;
            }

            if (
                event.type === 'click' ||
                (event.type === 'keydown' &&
                    keyboardMatches((event as React.KeyboardEvent<HTMLElement>).nativeEvent, [
                        keyboardKeys.Enter,
                        keyboardKeys.Space,
                    ]) &&
                    hasFocusRef.current)
            ) {
                !isEventFromInteractiveComponent(event) && onClick(event);
            }
        },
        [onClick, disabled, forceDisabled]
    );
    const isPressEnabled = pressEnabled || !!onClick;
    const isHoverEnabled = hoverEnabled || isPressEnabled;

    const paddingProps = genPaddingProps({
        Left: paddingLeft,
        Right: paddingRight,
        Top: paddingTop,
        Bottom: paddingBottom,
    });
    const radiusProps = genBorderRadiusProp({
        borderRadius,
        borderBottomLeftRadius,
        borderBottomRightRadius,
        borderTopLeftRadius,
        borderTopRightRadius,
    });
    const additionalProps: Record<string, unknown> = {};
    if (isPressEnabled && !forceDisabled && !disabled) {
        additionalProps.tabIndex = 0;
    }

    if (ref) {
        additionalProps.ref = ref;
    }

    return (
        <DisabledContext.Provider value={forceDisabled || !!disabled}>
            <Element
                {...restProps}
                {...additionalProps}
                onFocus={focusCallback}
                onBlur={blurCallback}
                onClick={activationHandler}
                onKeyDown={activationHandler}
                style={{ padding, ...paddingProps, ...radiusProps }}
                className={classnames(
                    styles.card,
                    styles[`card-style-${style}`],
                    !!hoverStyle && styles[`card-hover-style-${hoverStyle}`],
                    !!borderStyle && styles[`card-border-style-${borderStyle}`],
                    styles[`card-shadow-${shadow}`],
                    {
                        [styles.cardStretched]: stretched,
                        [styles.cardVerticalStretched]: verticalStretched,
                        [styles.cardPressEnabled]: isPressEnabled,
                        [styles.cardHoverEnabled]: isHoverEnabled,
                        [styles.increaseShadow]: increaseShadow,
                        [styles.cardDisabled]: !!disabled,
                        [styles.borderSelected]: borderWidth === 'selected',
                        [styles.borderDefault]: borderWidth === 'default',
                    }
                )}
            >
                <IconDynamic stretched>
                    <div className={styles.flexContainer}>
                        <TextDynamic stretched>{children}</TextDynamic>
                    </div>
                </IconDynamic>
                <div className={styles.borderElement} style={radiusProps} />
            </Element>
        </DisabledContext.Provider>
    );
};

const CardComponent: PolymorphicComponentWithRef<CardProps, 'div'> = forwardRef(CardForwardRefRenderFunc);

export const Card = enrichWithSkeleton(CardComponent, CardSkeleton);
